var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { alpha, createTheme } from '@mui/material/styles';
export var DEFAULT_COLOR_THEME = {
    primary: {
        main: '#001B44FF',
        dark: '#1F2D41FF',
        light: '#D8E2F2FF',
        contrastText: '#FFFFFFFF',
        hover: '#D8E2F2CC',
        selected: '#D8E2F280',
        focus: '#D8E2F2FF',
        focusVisible: '#D8E2F2FF',
        outlinedBorder: '#D8E2F280',
    },
    secondary: {
        main: '#21C9E3FF',
        dark: '#80E0F0FF',
        light: '#B3ECF6FF',
        contrastText: '#FFFFFFFF',
        hover: '#B3ECF6CC',
        selected: '#B3ECF680',
        focus: '#B3ECF6FF',
        focusVisible: '#B3ECF6FF',
        outlinedBorder: '#80E0F080',
    },
    text: {
        primary: '#1F2D41DE',
        secondary: '#1F2D4199',
        disabled: '#1F2D4161',
        grey: '#9E9E9E'
    },
};
export var CommonThemeColors = {
    white: '#FFFFFF',
    black: '#000000',
    sys: {
        primary: DEFAULT_COLOR_THEME.primary,
        secondary: DEFAULT_COLOR_THEME.secondary,
        text: DEFAULT_COLOR_THEME.text,
        error: {
            main: '#D32F2F',
            dark: '#C62828',
            light: '#EF5350',
            contrast: '#ffffff',
            background: '#FDEDED'
        },
        warning: {
            main: '#EF6C00',
            dark: '#E65100',
            light: '#FF9800',
            contrast: '#ffffff',
            background: '#FFF4E5'
        },
        info: {
            main: '#0288D1',
            dark: '#01579B',
            light: '#03A9F4',
            contrast: '#ffffff',
            background: '#E5F6FD'
        },
        success: {
            main: '#2E7D32',
            dark: '#1B5E20',
            light: '#4CAF50',
            contrast: '#ffffff',
            background: '#EDF7ED'
        },
        palette: {
            blueGray: {
                "50": "#ECEFF1",
                "600": '#546E7A'
            }
        },
        action: {
            focus: 'rgba(169, 196, 238, 0.12)',
            active: '#A9C4EE',
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
        },
        availability: {
            lightBlue: '#c2f8fc',
            lightRed: '#fff0f0',
            boldBlue: '#2F67D6',
            boldRed: '#d13438',
        }
    },
    ref: {
        primary: {
            primary0: '#000000',
            primary20: '#002E6B',
            primary40: '#0C5AC2',
            primary60: '#568EF8',
            primary80: '#AECEFF',
            primary90: '#D0E4FF',
            primary95: '#F3F7FC',
            primary100: '#FFFFFF'
        },
        secondary: {
            secondary0: '#000000',
            secondary20: '#293041',
            secondary40: '#8990A5',
            secondary60: '#BFCCDC',
            secondary80: '#AECEFF',
            secondary90: '#D8E2F2',
            secondary95: '#EDF4FF',
            secondary100: '#FFFFFF'
        },
        tertiary: {
            tertiary0: '#000000',
            tertiary20: '#492900',
            tertiary40: '#8A5100',
            tertiary60: '#CB8024',
            tertiary80: '#FFB86D',
            tertiary95: '#FFEEE0',
            tertiary100: '#FFFFFF'
        },
        error: {
            error0: '#000000',
            error20: '#690005',
            error40: '#BA1A1A',
            error60: '#FF5449',
            error80: '#FFB4AB',
            error95: '#FFEDEA',
            error100: '#FFFFFF'
        },
        neutral: {
            neutral0: '#000000',
            neutral20: '#303034',
            neutral40: '#5E5E62',
            neutral60: '#919094',
            neutral80: '#C7C6CA',
            neutral95: '#F2F0F4',
            neutral100: '#FFFFFF'
        },
        highlight: {
            light: {
                red: '#FFD9D9',
                blue: '#E3EBF8',
                green: '#E0F8CC',
                orange: '#FFEFDD',
                amber: '#FFE8DB',
                cyan: '#E9F7FF',
                purple: '#F2DBFF',
                yellow: '#FFFDD1',
                teal: '#E7FFF9',
                blueGrey: '#E5E9EC',
                pink: '#FFEBF8'
            },
            dark: {
                red: '#8B0909',
                blue: '#0309A1',
                green: '#04B62B',
                orange: '#FA8800',
                amber: '#FF4F2C',
                cyan: '#12B1F5',
                purple: '#890DEB',
                yellow: '#EEC800',
                teal: '#08C99B',
                pink: '#FFA3EB',
                greenVariant: '#B9F485',
                blueGrey: '#1B1B1F'
            }
        }
    },
    surfaces: {
        light: {
            surface1: '#F2F5FC',
            surface2: '#ECF1FA',
            surface3: '#E4EDF7',
            surface4: '#E1ECF6',
            surface5: '#DDEAF5'
        },
        dark: {
            surface1: '#23242A',
            surface2: '#23242A',
            surface3: '#23242A',
            surface4: '#23242A',
            surface5: '#23242A'
        }
    },
    stateLayers: {
        light: {
            onSurface: {
                opacity008: 'rgba(27, 27, 31, 0.08)'
            },
            onSecondaryContainer: {
                opacity008: 'rgba(20, 27, 44, 0.08)'
            }
        }
    }
};
export var ColorPalette = {
    green: {
        backgroundColor: CommonThemeColors.ref.highlight.light.green,
        color: CommonThemeColors.ref.highlight.dark.green
    },
    orange: {
        backgroundColor: CommonThemeColors.ref.highlight.light.orange,
        color: CommonThemeColors.ref.highlight.dark.orange
    },
    amber: {
        backgroundColor: CommonThemeColors.ref.highlight.light.amber,
        color: CommonThemeColors.ref.highlight.dark.amber
    },
    cyan: {
        backgroundColor: CommonThemeColors.ref.highlight.light.cyan,
        color: CommonThemeColors.ref.highlight.dark.cyan
    },
    purple: {
        backgroundColor: CommonThemeColors.ref.highlight.light.purple,
        color: CommonThemeColors.ref.highlight.dark.purple
    },
    yellow: {
        backgroundColor: CommonThemeColors.ref.highlight.light.yellow,
        color: CommonThemeColors.ref.highlight.dark.yellow
    },
    teal: {
        backgroundColor: CommonThemeColors.ref.highlight.light.teal,
        color: CommonThemeColors.ref.highlight.dark.teal
    },
    blue: {
        backgroundColor: CommonThemeColors.ref.highlight.light.blue,
        color: CommonThemeColors.ref.highlight.dark.blue
    },
    red: {
        backgroundColor: CommonThemeColors.ref.highlight.light.red,
        color: CommonThemeColors.ref.highlight.dark.red
    },
    secondaryContainer: {
        backgroundColor: CommonThemeColors.ref.highlight.light.blueGrey,
        color: CommonThemeColors.ref.highlight.dark.blueGrey
    },
    blueGrey: {
        backgroundColor: CommonThemeColors.ref.highlight.light.blueGrey,
        color: CommonThemeColors.ref.highlight.dark.blueGrey
    },
};
var outlinedFieldPaddingY = {
    small: '9.9375px',
    smallMobile: '7.9375px',
    medium: '17.9375px',
};
var autocompleteOutlinedRootPaddingY = {
    small: '7.4375px',
    smallMobile: '5.4375px',
    medium: '10.4375px',
};
export var theme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
                // contained:{
                //     backgroundColor: CommonThemeColors.sys.secondary.main,
                // }
            },
            variants: [
                {
                    props: { variant: 'grayOutlined' },
                    style: function (_a) {
                        var theme = _a.theme;
                        return ({
                            border: "1px solid ".concat(alpha(theme.palette.text.primary, 0.23)),
                            color: theme.palette.text.secondary,
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                                backgroundColor: theme.palette.action.hover,
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.palette.action.disabledBackground,
                                color: theme.palette.text.disabled,
                            },
                        });
                    },
                },
            ],
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: CommonThemeColors.ref.neutral.neutral95
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: function (_a) {
                    var _b, _c, _d, _e;
                    var ownerState = _a.ownerState, theme = _a.theme;
                    return (__assign({ '& .MuiOutlinedInput-root': {
                            paddingTop: autocompleteOutlinedRootPaddingY.medium,
                            paddingBottom: autocompleteOutlinedRootPaddingY.medium,
                        }, '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': (_b = {
                                paddingTop: autocompleteOutlinedRootPaddingY.small,
                                paddingBottom: autocompleteOutlinedRootPaddingY.small
                            },
                            _b[theme.breakpoints.down('sm')] = {
                                paddingTop: autocompleteOutlinedRootPaddingY.smallMobile,
                                paddingBottom: autocompleteOutlinedRootPaddingY.smallMobile,
                            },
                            _b) }, (ownerState.multiple ? {
                        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': (_c = {
                                minHeight: 40,
                                paddingTop: 4,
                                paddingBottom: 4,
                                paddingLeft: 4
                            },
                            _c[theme.breakpoints.down('sm')] = {
                                minHeight: 36,
                                paddingTop: 2,
                                paddingBottom: 2,
                            },
                            _c),
                        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                            minWidth: 0,
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                        '& .MuiAutocomplete-tag': (_d = {
                                height: 30,
                                marginLeft: 0,
                                marginRight: '4px',
                                marginTop: '1px',
                                marginBottom: '1px',
                                maxWidth: 'calc(100% - 4px)'
                            },
                            _d[theme.breakpoints.down('sm')] = {
                                height: 26,
                            },
                            _d),
                        '& .MuiAutocomplete-tag .MuiChip-label': (_e = {},
                            _e[theme.breakpoints.down('sm')] = {
                                fontSize: 12,
                            },
                            _e),
                    } : {})));
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return ({
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            cursor: 'not-allowed',
                        },
                        '&.Mui-disabled input, &.Mui-disabled textarea, &.Mui-disabled .MuiSelect-select': {
                            cursor: 'not-allowed',
                        },
                        '&.Mui-disabled .MuiInputAdornment-root, &.Mui-disabled .MuiSvgIcon-root': {
                            cursor: 'not-allowed',
                        },
                    });
                },
                input: function (_a) {
                    var _b;
                    var ownerState = _a.ownerState, theme = _a.theme;
                    var disabledPlaceholder = {
                        '&.Mui-disabled::placeholder': {
                            color: theme.palette.text.disabled,
                            opacity: 1,
                            WebkitTextFillColor: theme.palette.text.disabled,
                        },
                    };
                    if (ownerState.multiline) {
                        return disabledPlaceholder;
                    }
                    if (ownerState.size === 'small') {
                        return __assign(__assign({}, disabledPlaceholder), (_b = { padding: "".concat(outlinedFieldPaddingY.small, " 14px") }, _b[theme.breakpoints.down('sm')] = {
                            padding: "".concat(outlinedFieldPaddingY.smallMobile, " 14px"),
                        }, _b));
                    }
                    return __assign(__assign({}, disabledPlaceholder), { padding: "".concat(outlinedFieldPaddingY.medium, " 14px") });
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: function (_a) {
                    var _b;
                    var theme = _a.theme;
                    return ({
                        '&.MuiSelect-outlined': {
                            paddingTop: outlinedFieldPaddingY.medium,
                            paddingBottom: outlinedFieldPaddingY.medium,
                        },
                        '.MuiInputBase-sizeSmall &.MuiSelect-outlined': (_b = {
                                paddingTop: outlinedFieldPaddingY.small,
                                paddingBottom: outlinedFieldPaddingY.small
                            },
                            _b[theme.breakpoints.down('sm')] = {
                                paddingTop: outlinedFieldPaddingY.smallMobile,
                                paddingBottom: outlinedFieldPaddingY.smallMobile,
                            },
                            _b),
                    });
                },
            },
        },
    },
    palette: {
        primary: {
            main: CommonThemeColors.sys.primary.main,
            dark: CommonThemeColors.sys.primary.dark,
            light: CommonThemeColors.sys.primary.light,
            "contrastText": CommonThemeColors.sys.primary.contrastText,
        },
        secondary: {
            main: CommonThemeColors.sys.secondary.main,
            dark: CommonThemeColors.sys.secondary.dark,
            light: CommonThemeColors.sys.secondary.light,
            contrastText: CommonThemeColors.sys.secondary.contrastText,
        },
        text: {
            primary: CommonThemeColors.sys.text.primary,
            secondary: CommonThemeColors.sys.text.secondary,
            disabled: CommonThemeColors.sys.text.disabled,
        },
        action: {
            disabled: CommonThemeColors.sys.action.disabled,
            disabledBackground: CommonThemeColors.sys.action.disabledBackground,
        },
        error: {
            main: CommonThemeColors.sys.error.main,
            dark: CommonThemeColors.sys.error.dark,
            light: CommonThemeColors.sys.error.light,
            contrastText: CommonThemeColors.sys.error.contrast,
        },
        warning: {
            main: CommonThemeColors.sys.warning.main,
            dark: CommonThemeColors.sys.warning.dark,
            light: CommonThemeColors.sys.warning.light,
            contrastText: CommonThemeColors.sys.warning.contrast,
        },
        info: {
            main: CommonThemeColors.sys.info.main,
            dark: CommonThemeColors.sys.info.dark,
            light: CommonThemeColors.sys.info.light,
            contrastText: CommonThemeColors.sys.info.contrast,
        },
        success: {
            main: CommonThemeColors.sys.success.main,
            dark: CommonThemeColors.sys.success.dark,
            light: CommonThemeColors.sys.success.light,
            contrastText: CommonThemeColors.sys.success.contrast,
        },
    },
    typography: {
        fontFamily: 'League Spartan, Arial, sans-serif',
        h1: {
            textAlign: 'start',
            fontSize: '60px',
            fontWeight: 300,
            "@media print": {
                fontSize: '1.4em'
            }
        },
        h2: {
            textAlign: 'start',
            fontSize: '48px',
            fontWeight: 300,
            "@media print": {
                fontSize: '1.2em'
            }
        },
        h3: {
            textAlign: 'start',
            fontSize: '34px',
            fontWeight: 400,
            "@media print": {
                fontSize: '1.0em'
            }
        },
        h4: {
            textAlign: 'start',
            fontSize: '24px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.9em'
            }
        },
        h5: {
            textAlign: 'start',
            fontSize: '20px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.7em'
            }
        },
        h6: {
            textAlign: 'start',
            fontSize: '16px',
            fontWeight: 500,
            "@media print": {
                fontSize: '0.7em'
            }
        },
        subtitle1: {
            textAlign: 'start',
            fontSize: '14px',
            lineHeight: '14px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.6em'
            }
        },
        subtitle2: {
            textAlign: 'start',
            fontSize: '12px',
            lineHeight: '12px',
            fontWeight: 500,
            "@media print": {
                fontSize: '0.6em'
            }
        },
        body1: {
            textAlign: 'start',
            fontSize: '14px',
            // lineHeight: '16px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.7em'
            }
        },
        body2: {
            textAlign: 'start',
            fontSize: '12px',
            lineHeight: '14px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.6em'
            }
        },
        caption: {
            textAlign: 'start',
            fontSize: '10px',
            lineHeight: '10px',
            fontWeight: 400,
            "@media print": {
                fontSize: '0.5em'
            }
        },
        button: {
            // textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            "@media print": {
                fontSize: '0.6em'
            }
        }
    },
});
